import React, { createContext, useContext, ReactNode } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Theme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import styled from 'styled-components'

const CustomModalContext = createContext<boolean | null>(null)

interface CustomModalProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  size?: 'small' | 'medium' | 'large' | `${number}px`
  positionTop?: boolean
  padding?: number | string
  style?: React.CSSProperties
}

interface StyledDialogProps {
  $width: number
}

interface StyledDialogContentProps {
  $width: number
}

const StyledDialog = styled(Dialog)<StyledDialogProps>`
  .MuiDialog-paper {
    border-radius: 4px;
    max-height: 99vh;
    width: ${(props) => props.$width}px;
  }
  .MuiBackdrop-root {
    background-color: rgba(0, 0, 0, 0.2);
  }
`

const StyledDialogContent = styled.div<StyledDialogContentProps>`
  padding: 4px;
  width: ${(props) => props.$width}px;
`

const StyledTitle = styled(Typography as any)`
  font-weight: 700;
  font-size: 18px;
  padding: 10px;
  letter-spacing: -0.16px;
`

const StyledCloseButton = styled(IconButton)`
  position: absolute;
  right: 8px;
  top: 8px;
`

const StyledFooterContainer = styled(DialogActions)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
`

export const CustomStyledModal: React.FC<CustomModalProps> = ({
  open,
  onClose,
  children,
  size = 'medium',
  positionTop = false,
  padding = 5,
  style,
}) => {
  const sizeMap: Record<string, number> = {
    small: 330,
    medium: 500,
    large: 800,
  }

  const width =
    typeof size === 'string' && size.endsWith('px') ? parseInt(size) : sizeMap[size] || 500

  return (
    <CustomModalContext.Provider value={true}>
      <Dialog style={style} open={open} onClose={onClose} maxWidth={false}>
        <StyledDialogContent $width={width}>{children}</StyledDialogContent>
      </Dialog>
    </CustomModalContext.Provider>
  )
}

const useCustomModalContext = () => {
  const context = useContext(CustomModalContext)
  if (!context) {
    throw new Error('This component must be used within a <CustomModal> component.')
  }
  return context
}

interface CustomModalHeaderProps {
  title?: string | React.ReactNode
  onClose?: () => void
  variant?:
    | 'display4'
    | 'display3'
    | 'display2'
    | 'display1'
    | 'headline'
    | 'title'
    | 'subheading'
    | 'body2'
    | 'body1'
    | 'caption'
    | 'button'
  titleSx?: React.CSSProperties
}

const CustomModalHeader: React.FC<CustomModalHeaderProps> = ({
  title: Title,
  onClose,
  variant = 'title',
  titleSx,
}) => {
  useCustomModalContext()

  return (
    <DialogTitle style={{ padding: 2 }}>
      <StyledTitle variant={variant} style={titleSx}>
        {typeof Title === 'string' ? <span>{Title}</span> : Title}
      </StyledTitle>
      {onClose && (
        <StyledCloseButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </StyledCloseButton>
      )}
    </DialogTitle>
  )
}

interface CustomModalContentProps {
  children: ReactNode
}

const CustomModalContent: React.FC<CustomModalContentProps> = ({ children }) => {
  useCustomModalContext()
  return <DialogContent style={{ overflow: 'visible' }}>{children}</DialogContent>
}

interface CustomModalFooterProps {
  onCancel?: () => void
  onConfirm?: () => void
  onConfirmColor?: 'primary' | 'secondary'
  cancelText?: string
  confirmText?: string
  disableConfirm?: boolean
  alignment?: 'flex-end' | 'space-between' | 'center'
  zIndex?: number
  children?: ReactNode
  justifyChildren?: 'flex-start' | 'center' | 'flex-end'
  direction?: 'row' | 'row-reverse'
  isLoading?: boolean
}

const CustomModalFooter: React.FC<CustomModalFooterProps> = ({
  onCancel,
  onConfirm,
  onConfirmColor = 'primary',
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  disableConfirm = false,
  alignment = 'flex-end',
  children,
  zIndex,
  direction = 'row',
  isLoading = false,
}) => {
  return (
    <StyledFooterContainer
      style={{
        justifyContent: alignment,
      }}
    >
      <div>
        {onCancel && (
          <Button onClick={onCancel} className="button-white-and-red-padded" disabled={isLoading}>
            {cancelText}
          </Button>
        )}
        {onConfirm && (
          <Button
            onClick={onConfirm}
            color={onConfirmColor}
            variant="contained"
            disabled={disableConfirm || isLoading}
          >
            {confirmText}
          </Button>
        )}
        {children}
      </div>
    </StyledFooterContainer>
  )
}

interface CustomModalComponent extends React.FC<CustomModalProps> {
  Header: typeof CustomModalHeader
  Content: typeof CustomModalContent
  Footer: typeof CustomModalFooter
}

export const CustomModal = CustomStyledModal as CustomModalComponent
CustomModal.Header = CustomModalHeader
CustomModal.Content = CustomModalContent
CustomModal.Footer = CustomModalFooter



// WEBPACK FOOTER //
// ./src/components/common/CustomModal.tsx